/**
 * techApi — Sprint 6.2 (App Técnico / Mobile Installer).
 *
 * Cliente HTTP do App Técnico. Separado do api.js do NOC de
 * propósito: o app mobile usa o header X-Tech-Token (token de menor
 * privilégio), nunca o admin token.
 *
 * O token técnico é guardado em memória de sessão (sessionStorage):
 * some quando o técnico fecha o navegador. É o suficiente para o uso
 * em campo e evita deixar credencial persistida no aparelho.
 */

const TOKEN_KEY = "ipwd_tech_token";

/** Lê o token técnico da sessão. */
export function getTechToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

/** Guarda o token técnico na sessão. */
export function setTechToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ambiente sem sessionStorage — ignora */
  }
}

export class TechApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "TechApiError";
    this.status = status;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const headers = { "X-Tech-Token": getTechToken() };
  const init = { method, headers, signal };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(path, init);
  } catch (e) {
    throw new TechApiError(
      "Falha de rede — verifique a conexão.", 0
    );
  }
  const ctype = res.headers.get("content-type") || "";
  const data = ctype.includes("application/json")
    ? await res.json().catch(() => null)
    : null;
  if (!res.ok) {
    const detail = (data && (data.detail || data.message))
      || `Erro ${res.status}`;
    throw new TechApiError(detail, res.status);
  }
  return data;
}

// ── Endpoints /api/tech/* ────────────────────────────────────────────

/** Valida o token abrindo uma sessão de teste leve (workorders). */
export async function techPing({ signal } = {}) {
  return request("/api/tech/workorders?limit=1", { signal });
}

export async function startSession(installer, teamId, { signal } = {}) {
  return request("/api/tech/sessions", {
    method: "POST", signal,
    body: { installer, team_id: teamId || null },
  });
}

export async function getSession(sessionId, { signal } = {}) {
  return request(
    `/api/tech/sessions/${encodeURIComponent(sessionId)}`,
    { signal }
  );
}

export async function scanQr(sessionId, qrSerial, { signal } = {}) {
  return request("/api/tech/scan", {
    method: "POST", signal,
    body: { session_id: sessionId, qr_serial: qrSerial },
  });
}

export async function searchCustomers(q, { signal } = {}) {
  return request(
    `/api/tech/customer-search?q=${encodeURIComponent(q)}`,
    { signal }
  );
}

export async function selectCustomer(payload, { signal } = {}) {
  return request("/api/tech/select-customer", {
    method: "POST", signal, body: payload,
  });
}

export async function captureLocation(payload, { signal } = {}) {
  return request("/api/tech/location", {
    method: "POST", signal, body: payload,
  });
}

export async function listWorkOrders(installer, { signal } = {}) {
  const qs = installer
    ? `?installer=${encodeURIComponent(installer)}`
    : "";
  return request(`/api/tech/workorders${qs}`, { signal });
}

export async function finishInstall(payload, { signal } = {}) {
  return request("/api/tech/install", {
    method: "POST", signal, body: payload,
  });
}
